import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "@/components/Logo";
import { List, X } from "@phosphor-icons/react";

const NAV = [
  { label: "Il Problema", href: "#problema" },
  { label: "Chi Siamo", href: "#chi-siamo" },
  { label: "Metodo", href: "#metodo" },
  { label: "Servizi", href: "#servizi" },
  { label: "Diagnosi", href: "#diagnosi" },
  { label: "Contatti", href: "#contatti" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const el = document.querySelector(href);
    if (!el) return;
    if (window.__lenis) {
      window.__lenis.scrollTo(el, { offset: -40, duration: 1.5 });
    } else {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-700 ${
        scrolled
          ? "backdrop-blur-md bg-navy-dark/70 border-b border-gold/15"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 flex items-center justify-between h-20">
        <Logo />

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((item, i) => (
            <a
              key={item.href}
              href={item.href}
              onClick={(e) => handleClick(e, item.href)}
              data-testid={`nav-link-${item.href.replace("#", "")}`}
              className="group relative text-[0.78rem] uppercase text-ink/70 hover:text-gold transition-colors duration-500"
              style={{ letterSpacing: "0.2em" }}
            >
              <span className="font-sans-crisp font-medium">
                <span className="text-gold/60 mr-2 font-display italic">0{i + 1}</span>
                {item.label}
              </span>
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-500 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="mailto:marco@mdconsultingsas.com"
            data-testid="nav-cta-email"
            className="hidden md:inline-flex items-center gap-2 text-[0.72rem] uppercase text-navy-dark bg-gold hover:bg-gold-light transition-colors duration-500 px-5 py-3 font-sans-crisp font-semibold"
            style={{ letterSpacing: "0.22em" }}
          >
            Contattaci
            <span aria-hidden>→</span>
          </a>

          <button
            className="lg:hidden text-gold p-2"
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            onClick={() => setOpen((s) => !s)}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={22} weight="light" /> : <List size={22} weight="light" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden bg-navy-dark border-t border-gold/15"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {NAV.map((item, i) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => handleClick(e, item.href)}
                  className="flex items-baseline gap-4 text-ink/85 hover:text-gold transition-colors"
                  data-testid={`nav-mobile-link-${item.href.replace("#", "")}`}
                >
                  <span className="text-gold/70 font-display italic text-sm">
                    0{i + 1}
                  </span>
                  <span
                    className="uppercase text-sm font-medium"
                    style={{ letterSpacing: "0.2em" }}
                  >
                    {item.label}
                  </span>
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
